import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  Collapse,
  Box,
} from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from "material-react-table";
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { getDefaultMRTOptions } from "../../dedicatedDelivery/DefaultMrtOptions";
import { toast } from "react-toastify";
import { getFdpListByClient } from "../../../app/features/fdp/fdpSlice";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { Client } from "../../../app/models/responseModels/clientsResponse";
import { ClientAgent } from "../../../app/api/clients/ClientAgent";
import { AddClientFdpTermRequest, UpdateClientFdpTermRequest } from "../../../app/models/requestModels/clientRequest";
import { ApiResponse } from "../../../app/models/ApiResponse";

// Define the interface for FDP data
interface Fdp {
  id: number;
  companyName: string;
}

// Define the interface for FDP payment terms data
interface FdpPaymentTerm {
  id: number;
  fdpId: number;
  fdpName: string;
  netPaymentTerm: number;
  earlyPaymentDiscount: number;
}

interface FdpNetPaymentTermsProps {
  clientData: Client;
}

const FdpNetPaymentTerms: React.FC<FdpNetPaymentTermsProps> = ({ clientData }) => {
  const fdps = useSelector((state: RootState) => state.fdp.fdpListByClient);
  const [isLoading, setIsLoading] = useState(false);

  // State for form inputs
  const [selectedFdp, setSelectedFdp] = useState<number | "">("");
  const [netPaymentTerm, setNetPaymentTerm] = useState<number | "">("");
  const [earlyPaymentDiscount, setEarlyPaymentDiscount] = useState<number | "">("");
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  // State for payment terms
  const [paymentTerms, setPaymentTerms] = useState<FdpPaymentTerm[]>([]);

  const defaultMRTOptions = getDefaultMRTOptions<FdpPaymentTerm>();
  const dispatch = useAppDispatch();

  // Get FDPs that are not already in payment terms (except when editing)
  const availableFdps = useMemo(() => {
    return fdps.filter((fdp: Fdp) => {
      // If editing, include the currently selected FDP (so it can be kept)
      if (isEditing && editId !== null) {
        const editingTerm = paymentTerms.find(term => term.id === editId);
        if (editingTerm && editingTerm.fdpId === fdp.id) {
          return true;
        }
      }
      // Otherwise, only include FDPs not already in payment terms
      return !paymentTerms.some(term => term.fdpId === fdp.id);
    });
  }, [fdps, paymentTerms, isEditing, editId]);

  // Function to fetch FDP list for the client
  const fetchFdpList = () => {
    const clientId = Number(clientData.id);
    if (!isNaN(clientId)) {
      dispatch(getFdpListByClient(clientId));
    }
  };

  // Function to fetch client data with FDP net payment terms
  const fetchClientWithFdpTerms = async () => {
    const clientId = Number(clientData.id);
    if (isNaN(clientId)) return;

    setIsLoading(true);
    try {
      // Use getClientById API call to fetch client data
      const response = await ClientAgent.getClientById(clientId);

      // Update payment terms from the response
      if (response.data.clientFdpNetpaymentTerms && Array.isArray(response.data.clientFdpNetpaymentTerms)) {
        const updatedTerms = response.data.clientFdpNetpaymentTerms.map(term => ({
          id: term.id,
          fdpId: term.fdpId,
          fdpName: term.fdpName,
          netPaymentTerm: term.netPaymentTerm,
          earlyPaymentDiscount: term.earlyPaymentDiscount
        }));
        setPaymentTerms(updatedTerms);
      }
    } catch (error) {
      console.error("Error fetching client data:", error);
      toast.error("Failed to fetch client data");
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data loading
  useEffect(() => {
    fetchFdpList();
    fetchClientWithFdpTerms();
  }, [dispatch, clientData.id]);

  // Clear form fields
  const clearForm = () => {
    setSelectedFdp("");
    setNetPaymentTerm("");
    setEarlyPaymentDiscount("");
    setIsEditing(false);
    setEditId(null);
    setIsFormOpen(false);
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate all fields are filled
    if (selectedFdp === "" || netPaymentTerm === "" || earlyPaymentDiscount === "") {
      toast.error("Please fill all fields");
      return;
    }

    // Validate negative net payment term
    if (netPaymentTerm as number < 0) {
      toast.error("Net payment term cannot be negative");
      return;
    }

    // Validate netPaymentTerm is <= 100
    if (netPaymentTerm as number > 100) {
      toast.error("Net payment term must be 100 days or less");
      return;
    }

    // Validate negative early payment discount
    if (earlyPaymentDiscount as number < 0) {
      toast.error("Early payment discount cannot be negative");
      return;
    }

    // Validate earlyPaymentDiscount is <= 100
    if (earlyPaymentDiscount as number > 100) {
      toast.error("Early payment discount must be 100% or less");
      return;
    }

    setIsLoading(true);

    try {
      const fdpName = fdps.find((fdp: Fdp) => fdp.id === selectedFdp)?.companyName || "";
      const clientId = Number(clientData.id);

      let requestData: AddClientFdpTermRequest | UpdateClientFdpTermRequest;
      let response: ApiResponse;

      if (isEditing && editId !== null) {
        // Updating requires 'id'
        requestData = {
          id: editId, // Ensure 'id' is included
          clientId,
          fdpId: selectedFdp as number,
          fdpName,
          netPaymentTerm: netPaymentTerm as number,
          earlyPaymentDiscount: earlyPaymentDiscount as number,
        };

        response = await ClientAgent.updateClientFdpTerm(requestData as UpdateClientFdpTermRequest);

        if (response) {
          toast.success("Payment term updated successfully");
          await fetchClientWithFdpTerms();
        } else {
          toast.error(response || "Failed to update payment term");
        }
      } else {
        // Adding does not require 'id'
        requestData = {
          clientId,
          fdpId: selectedFdp as number,
          fdpName,
          netPaymentTerm: netPaymentTerm as number,
          earlyPaymentDiscount: earlyPaymentDiscount as number,
        };

        response = await ClientAgent.addClientFdpTerm(requestData as AddClientFdpTermRequest);

        if (response) {
          toast.success("Payment term added successfully");
          await fetchClientWithFdpTerms();
        } else {
          toast.error("Failed to add payment term");
        }
      }

      clearForm();
    } catch (error) {
      console.error("Error submitting payment term:", error);
      toast.error("An error occurred while processing your request");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit action
  const handleEdit = (term: FdpPaymentTerm) => {
    setSelectedFdp(term.fdpId);
    setNetPaymentTerm(term.netPaymentTerm);
    setEarlyPaymentDiscount(term.earlyPaymentDiscount);
    setIsEditing(true);
    setEditId(term.id);
    setIsFormOpen(true);
  };

  // Handle delete action
  const handleDelete = async (id: number) => {
    try {
      setIsLoading(true);
      const response = await ClientAgent.deleteClientNetPaymentTerm(id);

      if (response) {
        toast.success("Payment term deleted successfully");
        await fetchClientWithFdpTerms();
      } else {
        toast.error("Failed to delete payment term");
      }
    } catch (error) {
      console.error("Error deleting payment term:", error);
      toast.error("An error occurred while deleting the payment term");
    } finally {
      setIsLoading(false);
    }
  };

  // Define table columns
  const columns = useMemo<MRT_ColumnDef<FdpPaymentTerm>[]>(
    () => [
      {
        id: "actions",
        header: "Actions",
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: "8px" }}>
            <IconButton
              color="primary"
              onClick={() => handleEdit(row.original)}
              size="small"
              disabled={isLoading}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="error"
              onClick={() => handleDelete(row.original.id)}
              size="small"
              disabled={isLoading}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ),
      },
      {
        accessorKey: "fdpName",
        header: "FDP Name",
      },
      {
        accessorKey: "netPaymentTerm",
        header: "Net Payment Term",
        Cell: ({ cell }) => `${cell.getValue()} days`,
      },
      {
        accessorKey: "earlyPaymentDiscount",
        header: "Early Payment Discount",
        Cell: ({ cell }) => `${cell.getValue()}%`,
      },
    ],
    [isLoading]
  );

  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    columns,
    data: paymentTerms,
    initialState: {
      ...defaultMRTOptions.initialState,
      sorting: [{ id: "fdpName", desc: false }],
    },
    state: {
      showProgressBars: isLoading,
    },
    enableRowActions: false,
    muiTableHeadProps: {
      sx: {
        "& .MuiTableCell-root": {
          backgroundColor: "grey.300",
          color: "grey.800",
          fontWeight: "bold",
        },
      },
    },
  });

  return (
    <Card>
      <CardHeader
        subheader="FDP Net Payment Terms"
        action={
          <IconButton
            color="primary"
            onClick={() => setIsFormOpen(!isFormOpen)}
            size="small"
            disabled={isLoading}
          >
            {isFormOpen ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
          </IconButton>
        }
      />
      <CardContent>
        <Collapse in={isFormOpen}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                {isEditing ? "Edit Payment Term" : "Add New Payment Term"}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    margin="normal"
                    id="fdpId"
                    label="FDP"
                    fullWidth
                    size="small"
                    value={selectedFdp}
                    onChange={(e) => setSelectedFdp(Number(e.target.value))}
                    disabled={isLoading || availableFdps.length === 0}
                  >
                    {availableFdps.length === 0 ? (
                      <MenuItem disabled value="">
                        No available FDPs
                      </MenuItem>
                    ) : (
                      availableFdps.map((fdp: Fdp) => (
                        <MenuItem key={fdp.id} value={fdp.id}>
                          {fdp.companyName}
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    margin="normal"
                    id="netPaymentTerm"
                    label="Net Payment Term (days)"
                    fullWidth
                    size="small"
                    type="number"
                    value={netPaymentTerm}
                    onChange={(e) => setNetPaymentTerm(e.target.value ? Number(e.target.value) : "")}
                    inputProps={{ min: 0, max: 100 }}
                    error={
                      netPaymentTerm !== "" && 
                      ((netPaymentTerm as number) < 0 || (netPaymentTerm as number) > 100)
                    }
                    helperText={
                      netPaymentTerm !== "" && (netPaymentTerm as number) < 0 
                        ? "Cannot be negative" 
                        : (netPaymentTerm !== "" && (netPaymentTerm as number) > 100 
                          ? "Must be 100 or less" 
                          : "")
                    }
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    margin="normal"
                    id="earlyPaymentDiscount"
                    label="Early Payment Discount (%)"
                    fullWidth
                    size="small"
                    type="number"
                    value={earlyPaymentDiscount}
                    onChange={(e) => setEarlyPaymentDiscount(e.target.value ? Number(e.target.value) : "")}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    error={
                      earlyPaymentDiscount !== "" && 
                      ((earlyPaymentDiscount as number) < 0 || (earlyPaymentDiscount as number) > 100)
                    }
                    helperText={
                      earlyPaymentDiscount !== "" && (earlyPaymentDiscount as number) < 0 
                        ? "Cannot be negative" 
                        : (earlyPaymentDiscount !== "" && (earlyPaymentDiscount as number) > 100 
                          ? "Must be 100% or less" 
                          : "")
                    }
                    disabled={isLoading}
                  />
                </Grid>
              </Grid>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleSubmit}
                  sx={{ mr: 2 }}
                  disabled={
                    isLoading ||
                    availableFdps.length === 0 ||
                    selectedFdp === "" ||
                    netPaymentTerm === "" ||
                    earlyPaymentDiscount === "" ||
                    (netPaymentTerm as number) < 0 ||
                    (netPaymentTerm as number) > 100 ||
                    (earlyPaymentDiscount as number) < 0 ||
                    (earlyPaymentDiscount as number) > 100
                  }
                >
                  {isEditing ? "Update" : "Add"}
                </Button>

                <Button
                  variant="outlined"
                  onClick={clearForm}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Collapse>

        <MaterialReactTable table={table} />
      </CardContent>
    </Card>
  );
};

export default FdpNetPaymentTerms;